<template>
    <transition name="action-panel-fade">
        <div
            v-if="modelValue"
            class="fixed inset-0 z-50 bg-gray-900 bg-opacity-40 mt-10"
            @click="chiudi"
        >
            <transition name="action-panel-slide">
                <aside
                    v-if="modelValue"
                    class="absolute right-0 mt-10 h-[calc(100vh-5rem)] w-full max-w-[60vw] bg-white shadow-2xl flex flex-col rounded-tl-xl"
                    @click.stop
                >
                    <!-- Header -->
                    <div class="border-b border-gray-200 px-6 py-5 bg-white">
                        <div class="flex items-start justify-between gap-4">
                            <div class="min-w-0">
                                <div class="flex flex-wrap items-center gap-2 mb-2">
                                    <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {{ headerSubtitle }}
                                    </span>

                                    <!-- Badge Tipo (Standard / Periodica) -->
                                    <span
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                        :class="formData.activity_type === 'periodic' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-700'"
                                    >
                                        {{ formData.activity_type === 'periodic' ? 'Periodica / Serie' : 'Standard' }}
                                    </span>

                                    <!-- Badge Stato -->
                                    <span
                                        v-if="formData.status"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                        :class="statusBadgeClass(formData.status)"
                                    >
                                        {{ statusLabel(formData.status) }}
                                    </span>

                                    <!-- Badge Priorità -->
                                    <span
                                        v-if="formData.priority"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                        :class="priorityBadgeClass(formData.priority)"
                                    >
                                        {{ formData.priority }}
                                    </span>

                                    <!-- Badge Figlia -->
                                    <span
                                        v-if="formData.parent_id"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                                    >
                                        Figlia
                                    </span>
                                </div>

                                <h2 class="text-xl font-semibold text-gray-900 truncate">
                                    {{ formData.title || headerDefaultTitle }}
                                </h2>

                                <p class="text-sm text-gray-500 mt-1 truncate">
                                    <span v-if="formData.parent_id">
                                        Figlia di: {{ nomeAttivita(formData.parent_id) }}
                                    </span>
                                    <span v-else-if="isOrdineContext && selectedRiga">
                                        Riga: {{ selectedRiga.Description || selectedRiga.Item }}
                                    </span>
                                    <span v-else-if="isOrdineContext">
                                        Attività generale ordine / contratto
                                    </span>
                                    <span v-else-if="formData.company_name">
                                        Cliente: {{ formData.company_name }}
                                    </span>
                                    <span v-else>
                                        {{ isEdit ? 'Modifica dati' : 'Nuovo elemento' }}
                                    </span>
                                </p>
                            </div>

                            <button
                                type="button"
                                @click="chiudi"
                                class="rounded-full p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                title="Chiudi"
                            >
                                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Body -->
                    <div ref="modalBody" class="flex-1 overflow-y-auto px-6 py-6 bg-white">
                        <div v-if="formData.parent_id" class="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                            Figlia di: <strong>{{ nomeAttivita(formData.parent_id) }}</strong>
                        </div>

                        <div v-if="formError" class="mb-5 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                            <p class="font-medium">{{ formError }}</p>
                            <ul v-if="formErrorDetails && formErrorDetails.length" class="mt-1.5 list-disc list-inside space-y-0.5 text-xs">
                                <li v-for="(detail, i) in formErrorDetails" :key="i">{{ detail }}</li>
                            </ul>
                        </div>

                        <div class="space-y-6">
                            <!-- ================= SEZIONE: DATI PRINCIPALI ================= -->
                            <section>
                                <div class="flex items-center gap-3 mb-4">
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                    <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                        Dati principali
                                    </span>
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <!-- Tipo di Attività (Standard vs Periodica) -->
                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Tipologia attività *</label>
                                        <select
                                            v-model="formData.activity_type"
                                            :disabled="isEdit || isReminderMode"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                                            @change="onActivityTypeChange"
                                        >
                                            <option value="standard">Standard (Attività singola)</option>
                                            <option value="periodic">Periodica (Serie ricorrente)</option>
                                        </select>
                                    </div>

                                    <!-- Riga ordine / contratto (se contesto ordine) -->
                                    <div v-if="isOrdineContext" class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            {{ formData.activity_type === 'periodic' ? 'Riga contratto / ordine' : 'Riga ordine' }}
                                        </label>
                                        <select
                                            v-model="formData.row_id"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option :value="null">— Nessuna (generale ordine / contratto) —</option>
                                            <option v-for="r in righe" :key="r.row_id" :value="r.row_id">
                                                {{ r.Description || r.Item }} ({{ r.Item }})
                                            </option>
                                        </select>
                                    </div>

                                    <!-- Cliente / Commessa (solo contesto standalone) -->
                                    <template v-else>
                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                                            <v-select
                                                :options="foundClients"
                                                :filterable="false"
                                                v-model="selectedClient"
                                                label="name"
                                                @search="searchClients"
                                                @option:selected="onClientSelect"
                                            >
                                                <template #no-options="{ search, searching }">
                                                    <div v-if="searching">Ricerca in corso...</div>
                                                    <div v-else-if="!search">Digita per cercare un cliente</div>
                                                </template>
                                            </v-select>
                                        </div>

                                        <div class="md:col-span-2">
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Commessa</label>
                                            <select
                                                v-model="formData.job_id"
                                                @change="onJobSelect"
                                                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                :disabled="!formData.client_id"
                                            >
                                                <option :value="null">— Nessuna —</option>
                                                <option v-for="job in jobs" :key="job.id" :value="job.id">{{ job.name }}</option>
                                            </select>
                                        </div>
                                    </template>

                                    <!-- Attività padre (solo per attività standard) -->
                                    <div v-if="formData.activity_type === 'standard'" class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Attività padre</label>
                                        <select
                                            v-model="formData.parent_id"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option :value="null">— Nessuna (attività radice) —</option>
                                            <option v-for="a in attivitaSelezionabiliComePadre" :key="a.id" :value="a.id">
                                                {{ a.title }}
                                            </option>
                                        </select>
                                    </div>

                                    <!-- Titolo -->
                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                                        <input
                                            v-model="formData.title"
                                            type="text"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            :placeholder="'Titolo'"
                                        />
                                    </div>

                                    <!-- Descrizione -->
                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                                        <textarea
                                            v-model="formData.description"
                                            rows="3"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Descrizione"
                                        ></textarea>
                                    </div>
                                </div>
                            </section>

                            <!-- ================= SEZIONE: PERIODICA / SERIE ================= -->
                            <template v-if="formData.activity_type === 'periodic'">
                                <!-- Pianificazione Serie -->
                                <section>
                                    <div class="flex items-center gap-3 mb-4">
                                        <div class="h-px flex-1 bg-gray-200"></div>
                                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                            Pianificazione
                                        </span>
                                        <div class="h-px flex-1 bg-gray-200"></div>
                                    </div>

                                    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                                        <!-- Modalità pianificazione -->
                                        <div class="xl:col-span-2">
                                            <label class="block min-h-[20px] text-sm font-medium text-gray-700 mb-2">
                                                Modalità pianificazione
                                            </label>
                                            <div class="flex h-[42px] rounded-md border border-gray-300 overflow-hidden text-sm">
                                                <button
                                                    type="button"
                                                    @click="formData.scheduling_mode = 'duration'"
                                                    :class="[formData.scheduling_mode === 'duration' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50']"
                                                    class="flex-1 px-3 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                                                >
                                                    Durata prevista
                                                </button>
                                                <button
                                                    type="button"
                                                    @click="formData.scheduling_mode = 'date_range'"
                                                    :class="[formData.scheduling_mode === 'date_range' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50']"
                                                    class="flex-1 px-3 border-l border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                                                >
                                                    Ora inizio / fine
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Ora inizio (sempre visibile) -->
                                        <div>
                                            <label class="block min-h-[20px] text-sm font-medium text-gray-700 mb-2">
                                                Ora inizio predefinita
                                            </label>
                                            <VueDatePicker
                                                v-model="seriesStartTimePickerValue"
                                                time-picker
                                                is-24
                                                :minutes-increment="5"
                                                minutes-grid-increment="1"
                                                select-text="Conferma"
                                                cancel-text="Annulla"
                                                class="vue-datepicker-tailwind w-full"
                                            />
                                        </div>

                                        <!-- Durata prevista -->
                                        <template v-if="formData.scheduling_mode === 'duration'">
                                            <div>
                                                <label class="block min-h-[20px] text-sm font-medium text-gray-700 mb-2">
                                                    Durata prevista
                                                </label>
                                                <div class="relative">
                                                    <input
                                                        v-model.number="formData.default_duration_hours"
                                                        type="number"
                                                        min="0.25"
                                                        step="0.25"
                                                        class="w-full h-[42px] border border-gray-300 rounded-md px-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="Durata prevista"
                                                    />
                                                    <span class="absolute inset-y-0 right-3 flex items-center text-sm text-gray-500 pointer-events-none">
                                                        ore
                                                    </span>
                                                </div>
                                            </div>

                                            <div>
                                                <label class="block min-h-[20px] text-sm font-medium text-gray-700 mb-2">
                                                    Modalità allocazione
                                                </label>
                                                <select
                                                    v-model="formData.allocation_mode"
                                                    class="w-full h-[42px] border border-gray-300 rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    <option value="segmented">Segmentata</option>
                                                    <option value="monolithic">Monolitica</option>
                                                </select>
                                            </div>
                                        </template>

                                        <!-- Orario fine -->
                                        <template v-if="formData.scheduling_mode === 'date_range'">
                                            <div>
                                                <label class="block min-h-[20px] text-sm font-medium text-gray-700 mb-2">
                                                    Ora fine predefinita
                                                </label>
                                                <VueDatePicker
                                                    v-model="seriesEndTimePickerValue"
                                                    time-picker
                                                    is-24
                                                    :minutes-increment="5"
                                                    minutes-grid-increment="1"
                                                    select-text="Conferma"
                                                    cancel-text="Annulla"
                                                    class="vue-datepicker-tailwind w-full"
                                                />
                                            </div>
                                        </template>
                                    </div>
                                </section>

                                <!-- Ricorrenza Serie -->
                                <section>
                                    <div class="flex items-center gap-3 mb-4">
                                        <div class="h-px flex-1 bg-gray-200"></div>
                                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                            Ricorrenza
                                        </span>
                                        <div class="h-px flex-1 bg-gray-200"></div>
                                    </div>

                                    <div class="mb-4">
                                        <div class="flex rounded-md border border-gray-300 overflow-hidden text-sm">
                                            <button
                                                type="button"
                                                @click="formData.recurrence_mode = 'fixed'"
                                                :class="formData.recurrence_mode === 'fixed' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                                class="flex-1 px-3 py-2 transition-colors"
                                            >
                                                Periodicità fissa
                                            </button>
                                            <button
                                                type="button"
                                                @click="formData.recurrence_mode = 'manual'"
                                                :class="formData.recurrence_mode === 'manual' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                                class="flex-1 px-3 py-2 border-l border-gray-300 transition-colors"
                                            >
                                                Date manuali
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Periodicità fissa -->
                                    <div v-if="formData.recurrence_mode === 'fixed'" class="space-y-4">
                                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-end">
                                            <div>
                                                <label class="block mb-2 text-sm font-medium text-gray-700">
                                                    Prima data *
                                                </label>
                                                <input
                                                    v-model="formData.starts_on"
                                                    type="date"
                                                    class="w-full h-[42px] px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    @change="calcolaAnteprima"
                                                />
                                            </div>

                                            <div>
                                                <label class="block mb-2 text-sm font-medium text-gray-700">
                                                    Ripeti ogni
                                                </label>
                                                <div class="flex gap-2">
                                                    <input
                                                        v-model.number="formData.recurrence_interval"
                                                        type="number"
                                                        min="1"
                                                        class="w-24 h-[42px] px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        @change="calcolaAnteprima"
                                                    />
                                                    <select
                                                        v-model="formData.recurrence_unit"
                                                        class="flex-1 h-[42px] px-3 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        @change="calcolaAnteprima"
                                                    >
                                                        <option value="day">Giorni</option>
                                                        <option value="week">Settimane</option>
                                                        <option value="month">Mesi</option>
                                                        <option value="year">Anni</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Termine ricorrenza -->
                                        <div>
                                            <label class="block mb-2 text-sm font-medium text-gray-700">
                                                Termina
                                            </label>
                                            <div class="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
                                                <label class="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input
                                                        v-model="formData.end_type"
                                                        type="radio"
                                                        value="date"
                                                        class="text-blue-600"
                                                        @change="calcolaAnteprima"
                                                    />
                                                    <span class="whitespace-nowrap">
                                                        Il giorno
                                                    </span>
                                                    <input
                                                        v-model="formData.ends_on"
                                                        type="date"
                                                        :disabled="formData.end_type !== 'date'"
                                                        class="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                                                        @change="calcolaAnteprima"
                                                    />
                                                </label>

                                                <label class="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input
                                                        v-model="formData.end_type"
                                                        type="radio"
                                                        value="count"
                                                        class="text-blue-600"
                                                        @change="calcolaAnteprima"
                                                    />
                                                    <span>
                                                        Dopo
                                                    </span>
                                                    <input
                                                        v-model.number="formData.occurrences_count"
                                                        type="number"
                                                        min="1"
                                                        :max="maxOccurrences"
                                                        :disabled="formData.end_type !== 'count'"
                                                        class="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                                                        @change="calcolaAnteprima"
                                                    />
                                                    <span>
                                                        occorrenze
                                                    </span>
                                                </label>
                                            </div>
                                        </div>

                                        <!-- Anteprima date generate -->
                                        <div
                                            v-if="previewDates.length > 0"
                                            class="p-3 bg-gray-50 rounded-lg border border-gray-200"
                                        >
                                            <div class="flex items-center justify-between mb-2">
                                                <span class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    Anteprima date ({{ previewDates.length }})
                                                </span>
                                                <span
                                                    v-if="previewDates.length >= maxOccurrences"
                                                    class="text-xs text-amber-600 font-medium"
                                                >
                                                    Limite massimo raggiunto
                                                </span>
                                            </div>

                                            <div class="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                                                <span
                                                    v-for="(date, i) in previewDates"
                                                    :key="i"
                                                    class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-white border border-gray-200 text-gray-700 font-mono"
                                                >
                                                    {{ formatDateDisplay(date) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Date manuali -->
                                    <div v-else class="space-y-3">
                                        <div
                                            v-if="formData.manual_dates.length === 0"
                                            class="text-sm text-gray-400 italic py-2"
                                        >
                                            Nessuna data manuale inserita. Clicca "+ Aggiungi data" per iniziare.
                                        </div>

                                        <div
                                            v-for="(entry, index) in formData.manual_dates"
                                            :key="index"
                                            class="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2"
                                        >
                                            <div class="flex items-center justify-between">
                                                <span class="text-xs font-semibold text-gray-600 uppercase">
                                                    Data #{{ index + 1 }}
                                                </span>
                                                <button
                                                    type="button"
                                                    @click="removeManualDate(index)"
                                                    class="text-xs text-red-500 hover:text-red-700"
                                                >
                                                    Rimuovi
                                                </button>
                                            </div>

                                            <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                <div>
                                                    <label class="block text-xs text-gray-500 mb-0.5">
                                                        Data *
                                                    </label>
                                                    <input
                                                        v-model="entry.date"
                                                        type="date"
                                                        class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>

                                                <div>
                                                    <label class="block text-xs text-gray-500 mb-0.5">
                                                        Ora inizio
                                                    </label>
                                                    <input
                                                        v-model="entry.start_time"
                                                        type="time"
                                                        class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>

                                                <div>
                                                    <label class="block text-xs text-gray-500 mb-0.5">
                                                        Durata (ore)
                                                    </label>
                                                    <input
                                                        v-model.number="entry.duration_hours"
                                                        type="number"
                                                        min="0.25"
                                                        step="0.25"
                                                        placeholder="es. 2.5"
                                                        class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label class="block text-xs text-gray-500 mb-0.5">
                                                    Note
                                                </label>
                                                <input
                                                    v-model="entry.notes"
                                                    type="text"
                                                    placeholder="Note opzionali per questa occorrenza"
                                                    class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            @click="addManualDate"
                                            class="w-full py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                        >
                                            + Aggiungi data manuale
                                        </button>
                                    </div>

                                    <!-- Modifica serie: Propaga modifiche future -->
                                    <div
                                        v-if="isSeriesEdit"
                                        class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md"
                                    >
                                        <label class="flex items-start gap-2 cursor-pointer text-sm text-blue-900">
                                            <input
                                                v-model="formData.propagate_future"
                                                type="checkbox"
                                                class="rounded border-gray-300 text-blue-600 mt-0.5"
                                            />
                                            <span>
                                                <strong>Propaga modifiche alle attività future</strong>:
                                                aggiorna le date e i dettagli delle occorrenze future non ancora completate o personalizzate.
                                            </span>
                                        </label>
                                    </div>
                                </section>
                            </template>

                            <!-- ================= SEZIONE: STANDARD (ATTIVITÀ SINGOLA) ================= -->
                            <template v-else>
                                <!-- Pianificazione Standard -->
                                <section>
                                    <div class="flex items-center gap-3 mb-4">
                                        <div class="h-px flex-1 bg-gray-200"></div>
                                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                            Pianificazione
                                        </span>
                                        <div class="h-px flex-1 bg-gray-200"></div>
                                    </div>

                                    <div class="space-y-4">
                                        <div v-if="!isReminderMode">
                                            <label class="block text-sm font-medium text-gray-700 mb-2">Modalità pianificazione</label>
                                            <div class="flex rounded-md border border-gray-300 overflow-hidden text-sm">
                                                <button
                                                    type="button"
                                                    @click="formData.scheduling_mode = 'duration'"
                                                    :class="formData.scheduling_mode === 'duration' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                                    class="flex-1 px-3 py-2 transition-colors"
                                                >
                                                    Durata prevista
                                                </button>
                                                <button
                                                    type="button"
                                                    @click="formData.scheduling_mode = 'date_range'"
                                                    :class="formData.scheduling_mode === 'date_range' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                                    class="flex-1 px-3 py-2 border-l border-gray-300 transition-colors"
                                                >
                                                    Data inizio / fine
                                                </button>
                                            </div>
                                        </div>

                                        <div v-if="isReminderMode" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Data inizio</label>
                                                <div class="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700">
                                                    {{ formData.start_date }}
                                                </div>
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Ora inizio</label>
                                                <div class="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700">
                                                    {{ formData.start_datetime }}
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Data inizio</label>
                                                <VueDatePicker
                                                    v-model="formData.start_date"
                                                    @update:model-value="updateDeliveryDate"
                                                    format="dd/MM/yyyy"
                                                    value-format="yyyy-MM-dd"
                                                    :enable-time-picker="false"
                                                    :disabled-week-days="[0, 6]"
                                                    auto-apply
                                                    class="vue-datepicker-tailwind"
                                                />
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Ora inizio</label>
                                                <VueDatePicker
                                                    v-model="startTimePickerValue"
                                                    time-picker
                                                    is-24
                                                    :minutes-increment="5"
                                                    minutes-grid-increment="1"
                                                    select-text="Conferma"
                                                    cancel-text="Annulla"
                                                    class="vue-datepicker-tailwind"
                                                />
                                            </div>
                                        </div>

                                        <div v-if="formData.scheduling_mode === 'duration'" class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Durata prevista (ore) *</label>
                                                <input
                                                    v-model.number="formData.duration_hours"
                                                    type="number"
                                                    min="0.5"
                                                    step="0.5"
                                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="es. 6"
                                                />
                                                <p class="text-xs text-gray-400 mt-1" v-if="isReminderMode">
                                                    L'attività viene inserita a partire dall'ora indicata, senza verifiche di disponibilità.
                                                </p>
                                                <p class="text-xs text-gray-400 mt-1" v-else>
                                                    Il sistema alloca automaticamente l'attività nelle ore lavorative disponibili a partire dalla data e ora di inizio.
                                                </p>
                                            </div>

                                            <div v-if="!isReminderMode">
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Modalità allocazione</label>
                                                <div class="flex rounded-md border border-gray-300 overflow-hidden text-sm">
                                                    <button
                                                        type="button"
                                                        @click="formData.allocation_mode = 'segmented'"
                                                        :class="formData.allocation_mode === 'segmented' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                                        class="flex-1 px-3 py-2 transition-colors"
                                                    >
                                                        Segmentata
                                                    </button>
                                                    <button
                                                        type="button"
                                                        @click="formData.allocation_mode = 'monolithic'"
                                                        :class="formData.allocation_mode === 'monolithic' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                                        class="flex-1 px-3 py-2 border-l border-gray-300 transition-colors"
                                                    >
                                                        Monolitica
                                                    </button>
                                                </div>
                                                <p class="text-xs text-gray-400 mt-1">
                                                    <template v-if="formData.allocation_mode === 'segmented'">Distribuisce la durata su più slot lavorativi disponibili.</template>
                                                    <template v-else>Richiede un unico blocco continuo. Se non disponibile, il salvataggio fallisce.</template>
                                                </p>
                                            </div>
                                        </div>

                                        <div v-if="formData.scheduling_mode === 'date_range'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Data fine</label>
                                                <VueDatePicker
                                                    v-model="formData.end_date"
                                                    format="dd/MM/yyyy"
                                                    value-format="yyyy-MM-dd"
                                                    :enable-time-picker="false"
                                                    :disabled-week-days="[0, 6]"
                                                    auto-apply
                                                    class="vue-datepicker-tailwind"
                                                />
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Ora fine</label>
                                                <VueDatePicker
                                                    v-model="endTimePickerValue"
                                                    time-picker
                                                    is-24
                                                    :minutes-increment="5"
                                                    minutes-grid-increment="1"
                                                    cancel-text="Annulla"
                                                    class="vue-datepicker-tailwind"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Data consegna</label>
                                            <VueDatePicker
                                                v-model="formData.delivery_date"
                                                format="dd/MM/yyyy"
                                                value-format="yyyy-MM-dd"
                                                :enable-time-picker="false"
                                                :disabled-week-days="[0, 6]"
                                                auto-apply
                                                cancel-text="Annulla"
                                                class="vue-datepicker-tailwind"
                                            />
                                        </div>
                                    </div>
                                </section>

                            </template>

                            <!-- ================= SEZIONE: PRIORITÀ E STATO ================= -->
                            <section>
                                <div class="flex items-center gap-3 mb-4">
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                    <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                        Stato
                                    </span>
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Priorità *</label>
                                        <select
                                            v-model="formData.priority"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="bassa">Bassa</option>
                                            <option value="media">Media</option>
                                            <option value="alta">Alta</option>
                                            <option value="critica">Critica</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            {{ formData.activity_type === 'periodic' ? 'Stato serie' : 'Stato' }}
                                        </label>
                                        <select
                                            v-model="formData.status"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                                                {{ opt.label }}
                                            </option>
                                        </select>
                                        <p v-if="formData.activity_type === 'periodic'" class="text-xs text-gray-400 mt-1">
                                            Una serie sospesa non genera nuove attività. La priorità viene ereditata dalle attività generate.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <!-- ================= SEZIONE: ASSEGNAZIONE ================= -->
                            <section v-if="isReminderMode">
                                <div class="flex items-center gap-3 mb-4">
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                    <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                        Assegnazione
                                    </span>
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div v-if="canManageActivities" class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Reparto</label>
                                        <select
                                            v-model="formData.department_id"
                                            @change="onDepartmentChange"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option :value="null">— Nessun reparto —</option>
                                            <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                                                {{ dept.name }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Assegnatario</label>
                                        <div class="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700">
                                            {{ currentUserName }}
                                        </div>
                                        <p class="mt-1 text-xs text-gray-400">
                                            Chi prende in carico il promemoria diventa automaticamente l'assegnatario dell'attività.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <!-- ================= SEZIONE: ASSEGNAZIONE (standard) ================= -->
                            <section v-if="!isReminderMode && canManageActivities">
                                <div class="flex items-center gap-3 mb-4">
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                    <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                        Assegnazione
                                    </span>
                                    <div class="h-px flex-1 bg-gray-200"></div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Reparto</label>
                                        <select
                                            v-model="formData.department_id"
                                            @change="onDepartmentChange"
                                            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option :value="null">— Nessun reparto —</option>
                                            <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                                                {{ dept.name }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            Assegnatari
                                            <span v-if="loadingDeptUsers" class="text-gray-400 font-normal ml-1">(caricamento...)</span>
                                        </label>
                                        <div v-if="(localAssignableUsers || []).length === 0 && !loadingDeptUsers" class="text-xs text-gray-400 italic">
                                            Nessun utente disponibile.
                                        </div>
                                        <div class="space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                                            <label
                                                v-for="user in localAssignableUsers"
                                                :key="user.id"
                                                class="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 rounded"
                                            >
                                                <input
                                                    type="checkbox"
                                                    :value="user.id"
                                                    v-model="formData.owners"
                                                    class="rounded border-gray-300 text-blue-600"
                                                />
                                                {{ user.name }}
                                            </label>
                                        </div>
                                        <p class="mt-1 text-xs text-gray-400">
                                            Se nessun utente è selezionato ma è presente un reparto, verrà usato il manager come assegnatario di fallback.
                                        </p>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div class="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between gap-3">
                        <div v-if="formError" class="text-xs text-red-600 flex items-center gap-1.5 min-w-0">
                            <svg class="w-4 h-4 flex-shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span class="truncate font-medium">{{ formError }}</span>
                        </div>
                        <div v-else class="flex-1"></div>

                        <div class="flex items-center gap-3 flex-shrink-0">
                            <button
                                type="button"
                                @click="chiudi"
                                class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors text-sm"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                @click="salva"
                                :disabled="salvando"
                                class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {{ submitButtonLabel }}
                            </button>
                        </div>
                    </div>
                </aside>
            </transition>
        </div>
    </transition>
</template>

<script>
import axios from 'axios';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    name: 'ModaleAttivita',

    components: { VueDatePicker },

    props: {
        modelValue: { type: Boolean, default: false },
        // Attività standard da modificare (null = creazione)
        attivita: { type: Object, default: null },
        // Serie periodica da modificare (null = creazione)
        serie: { type: Object, default: null },
        // Tipologia predefinita all'apertura ('standard' | 'periodic')
        defaultActivityType: { type: String, default: 'standard' },
        // Attività padre, quando si crea una figlia
        parentActivity: { type: Object, default: null },
        // Contesto ordine o contratto
        ordine: { type: Object, default: null },
        righe: { type: Array, default: () => [] },
        // Riga preselezionata all'apertura
        defaultRowId: { type: [String, Number], default: null },
        // Data/ora di partenza preselezionata (es. click sul calendario)
        initialStart: { type: [String, Date], default: null },
        // Elenco piatto di tutte le attività (per selettore padre)
        allAttivita: { type: Array, default: () => [] },
        departments: { type: Array, default: () => [] },
        canManageActivities: { type: Boolean, default: false },
        maxOccurrences: { type: Number, default: 200 },
        // Promemoria da convertire in attività (presa in carico)
        reminder: { type: Object, default: null },
    },

    emits: ['update:modelValue', 'saved', 'close'],

    data() {
        return {
            formData: this.emptyForm(),
            formError: null,
            formErrorDetails: [],
            salvando: false,
            localAssignableUsers: [],
            loadingDeptUsers: false,
            startTimePickerValue: null,
            endTimePickerValue: null,
            seriesStartTimePickerValue: null,
            seriesEndTimePickerValue: null,
            foundClients: [],
            selectedClient: null,
            jobs: [],
            previewDates: [],
        };
    },

    computed: {
        isOrdineContext() {
            return !!this.ordine;
        },
        isReminderMode() {
            return !!this.reminder;
        },
        currentUserName() {
            return this.$page.props.auth?.user?.name || '—';
        },
        isSeriesEdit() {
            return !!(this.serie && (this.serie.id || this.formData.activity_type === 'periodic' && this.formData.id));
        },
        isActivityEdit() {
            return !!(this.attivita && this.attivita.id && this.formData.activity_type === 'standard');
        },
        isEdit() {
            return this.isSeriesEdit || this.isActivityEdit || !!this.formData.id;
        },
        headerSubtitle() {
            if (this.isReminderMode) return 'Presa in carico promemoria';
            if (this.isEdit) {
                return this.formData.activity_type === 'periodic' ? 'Modifica serie' : 'Modifica attività';
            }
            return this.formData.activity_type === 'periodic' ? 'Nuova serie' : 'Nuova attività';
        },
        headerDefaultTitle() {
            if (this.isReminderMode) return 'Presa in carico promemoria';
            if (this.isEdit) {
                return this.formData.activity_type === 'periodic' ? 'Modifica serie' : 'Modifica attività';
            }
            return this.formData.activity_type === 'periodic' ? 'Nuova serie' : 'Nuova attività';
        },
        selectedRiga() {
            if (!this.isOrdineContext || !this.formData.row_id) return null;
            return this.righe.find(r => r.row_id === this.formData.row_id) || null;
        },
        parentActivityData() {
            if (!this.formData.parent_id) return null;
            return this.allAttivita.find(a => a.id === this.formData.parent_id) || null;
        },
        attivitaSelezionabiliComePadre() {
            if (!this.isActivityEdit) return this.allAttivita;
            const excludedIds = new Set([this.formData.id]);
            let changed = true;
            while (changed) {
                changed = false;
                for (const a of this.allAttivita) {
                    if (a.parent_id && excludedIds.has(a.parent_id) && !excludedIds.has(a.id)) {
                        excludedIds.add(a.id);
                        changed = true;
                    }
                }
            }
            return this.allAttivita.filter(a => !excludedIds.has(a.id));
        },
        statusOptions() {
            if (this.formData.activity_type === 'periodic') {
                return [
                    { value: 'active', label: 'Attiva' },
                    { value: 'suspended', label: 'Sospesa' },
                ];
            }
            return [
                { value: 'nuova', label: 'Nuova' },
                { value: 'assegnata', label: 'Assegnata' },
                { value: 'schedulata', label: 'Schedulata' },
                { value: 'in_lavorazione', label: 'In lavorazione' },
                { value: 'completata', label: 'Completata' },
            ];
        },
        submitButtonLabel() {
            if (this.isReminderMode) {
                return this.salvando ? 'Presa in carico...' : 'Prendi in Carico';
            }
            if (this.salvando) return 'Salvataggio...';
            if (this.formData.activity_type === 'periodic') {
                if (this.isEdit) return 'Salva modifiche serie';
                if (this.formData.recurrence_mode === 'fixed' && this.previewDates.length > 0) {
                    return `Crea serie (${this.previewDates.length} attività)`;
                }
                if (this.formData.recurrence_mode === 'manual') {
                    const count = (this.formData.manual_dates || []).filter(d => d.date).length;
                    return count > 0 ? `Crea serie (${count} attività)` : 'Crea serie';
                }
                return 'Crea serie';
            }
            return this.isEdit ? 'Salva modifiche' : 'Salva attività';
        },
    },

    watch: {
        modelValue(val) {
            if (val) this.apri();
        },
        startTimePickerValue(val) {
            this.formData.start_datetime = this.pickerObjectToTimeString(val);
        },
        endTimePickerValue(val) {
            this.formData.end_datetime = this.pickerObjectToTimeString(val);
        },
        seriesStartTimePickerValue(val) {
            this.formData.default_start_time = this.pickerObjectToTimeString(val);
        },
        seriesEndTimePickerValue(val) {
            this.formData.default_end_time = this.pickerObjectToTimeString(val);
        },
    },

    methods: {
        emptyForm() {
            return {
                id: null,
                title: '',
                description: '',
                activity_type: 'standard', // 'standard' | 'periodic'

                // Contesto e Padre
                row_id: null,
                client_id: null,
                company_name: null,
                job_id: null,
                job_description: null,
                parent_id: null,
                priority: 'media',
                status: 'nuova',

                // Assegnazione
                department_id: null,
                owners: [],

                // Pianificazione Standard
                scheduling_mode: 'date_range',
                allocation_mode: 'segmented',
                start_date: null,
                start_datetime: null,
                end_date: null,
                end_datetime: null,
                duration_hours: null,
                delivery_date: null,

                // Configurazione Periodica / Serie (Sempre operativa di default)
                execution_mode: 'operational',
                default_duration_hours: null,
                default_start_time: null,
                default_end_time: null,
                recurrence_mode: 'fixed',
                recurrence_interval: 1,
                recurrence_unit: 'month',
                starts_on: null,
                ends_on: null,
                occurrences_count: 1,
                end_type: 'date',
                manual_dates: [],
                propagate_future: false,
            };
        },

        apri() {
            this.formError = null;
            this.formErrorDetails = [];
            this.foundClients = [];
            this.jobs = [];
            this.selectedClient = null;
            this.previewDates = [];

            if (this.serie) {
                this.popolaFormDaSerie(this.serie);
            } else if (this.attivita) {
                this.popolaFormDaAttivita(this.attivita);
            } else if (this.reminder) {
                this.popolaFormDaReminder(this.reminder);
            } else {
                const form = this.emptyForm();
                form.activity_type = this.defaultActivityType || 'standard';

                if (this.parentActivity) {
                    form.parent_id = this.parentActivity.id;
                    form.row_id = this.parentActivity.row_id ?? null;
                    if (this.parentActivity.end_date) {
                        const start = new Date(this.parentActivity.end_date.substring(0, 10) + 'T00:00:00');
                        start.setDate(start.getDate() + 1);
                        const end = new Date(start);
                        end.setDate(end.getDate() + 1);
                        form.start_date = start.toISOString().substring(0, 10);
                        form.end_date = end.toISOString().substring(0, 10);
                        form.delivery_date = end.toISOString().substring(0, 10);
                    }
                } else if (this.isOrdineContext && this.defaultRowId) {
                    const riga = this.righe.find(r => r.row_id === this.defaultRowId);
                    if (riga) {
                        form.row_id = riga.row_id;
                        form.title = riga.Description || riga.Item || '';
                        if (riga.ConfirmedDeliveryDate) {
                            const today = new Date();
                            const deliveryDate = new Date(riga.ConfirmedDeliveryDate);
                            form.delivery_date = deliveryDate < today
                                ? today.toISOString().substring(0, 10)
                                : riga.ConfirmedDeliveryDate.substring(0, 10);
                            form.starts_on = this.normalizeDateInput(riga.ConfirmedDeliveryDate);
                            form.ends_on = form.starts_on;
                        } else if (this.ordine.OrderDate) {
                            form.starts_on = this.normalizeDateInput(this.ordine.OrderDate);
                            form.ends_on = form.starts_on;
                        }
                    }
                } else if (this.isOrdineContext && this.ordine.OrderDate) {
                    form.starts_on = this.normalizeDateInput(this.ordine.OrderDate);
                    form.ends_on = form.starts_on;
                } else if (this.initialStart) {
                    const start = new Date(this.initialStart);
                    if (!Number.isNaN(start.getTime())) {
                        form.start_date = this.formatLocalDate(start);
                        form.starts_on = form.start_date;
                        form.ends_on = form.start_date;
                        form.scheduling_mode = 'duration';
                        form.duration_hours = 1;
                        form.default_duration_hours = 1;
                        form.default_start_time = this.startTimeFromInitialStart();
                    }
                }

                this.formData = form;
                const usaOrarioIniziale = this.initialStart && !this.parentActivity && !(this.isOrdineContext && this.defaultRowId);
                this.startTimePickerValue = usaOrarioIniziale
                    ? this.timeStringToPickerObject(this.startTimeFromInitialStart())
                    : null;
                this.endTimePickerValue = null;
                this.seriesStartTimePickerValue = usaOrarioIniziale
                    ? this.timeStringToPickerObject(this.startTimeFromInitialStart())
                    : null;
                this.seriesEndTimePickerValue = null;
            }

            this.allineaStato();

            if (this.formData.activity_type === 'periodic' && this.formData.recurrence_mode === 'fixed') {
                this.$nextTick(() => {
                    this.calcolaAnteprima();
                });
            }

            if (this.canManageActivities && !this.isReminderMode) {
                if (this.formData.department_id) {
                    this.caricaUtentiReparto(this.formData.department_id, this.formData.owners);
                } else {
                    this.loadAllAvailableUsers();
                }
            }
        },

        popolaFormDaAttivita(attivita) {
            const savedOwners = attivita.owners ? attivita.owners.map(o => o.id) : [];
            const form = this.emptyForm();
            Object.assign(form, {
                id: attivita.id,
                title: attivita.title,
                description: attivita.description || '',
                activity_type: 'standard',
                scheduling_mode: attivita.scheduling_mode || 'date_range',
                allocation_mode: attivita.allocation_mode || 'segmented',
                duration_hours: attivita.total_hours || null,
                start_date: attivita.start_date ? attivita.start_date.substring(0, 10) : null,
                end_date: attivita.end_date ? attivita.end_date.substring(0, 10) : null,
                delivery_date: attivita.delivery_date ? attivita.delivery_date.substring(0, 10) : null,
                priority: attivita.priority || 'media',
                status: attivita.status || 'nuova',
                parent_id: attivita.parent_id || null,
                department_id: attivita.department_id || null,
                owners: [],
                row_id: attivita.row_id || null,
                client_id: attivita.client_id || null,
                company_name: attivita.company_name || null,
                job_id: attivita.job_id || null,
                job_description: attivita.job || null,
            });
            this.formData = form;
            this.startTimePickerValue = this.timeStringToPickerObject(attivita.start_time);
            this.endTimePickerValue = this.timeStringToPickerObject(attivita.end_time);
            this.seriesStartTimePickerValue = null;
            this.seriesEndTimePickerValue = null;

            if (attivita.company_name) {
                this.selectedClient = { id: attivita.client_id, name: attivita.company_name };
            }

            if (this.canManageActivities) {
                if (attivita.department_id) {
                    this.caricaUtentiReparto(attivita.department_id, savedOwners);
                } else {
                    this.loadAllAvailableUsers().then(() => {
                        this.formData.owners = savedOwners;
                    });
                }
            }
        },

        popolaFormDaReminder(reminder) {
            const form = this.emptyForm();
            const currentUserId = this.$page.props.auth?.user?.id ?? null;
            Object.assign(form, {
                title: reminder.title || '',
                description: reminder.description || '',
                activity_type: 'standard',
                scheduling_mode: 'duration',
                allocation_mode: 'segmented',
                duration_hours: 1,
                priority: 'media',
                status: 'in_lavorazione',
                department_id: reminder.department_id ?? reminder.department?.id ?? null,
                client_id: reminder.cust_supp || null,
                company_name: reminder.company_name || null,
                owners: currentUserId !== null ? [currentUserId] : [],
            });

            const start = new Date();
            form.start_date = this.formatLocalDate(start);
            form.start_datetime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;

            this.formData = form;
            this.startTimePickerValue = this.timeStringToPickerObject(form.start_datetime);
            this.endTimePickerValue = null;
            this.seriesStartTimePickerValue = null;
            this.seriesEndTimePickerValue = null;

            if (reminder.cust_supp) {
                this.selectedClient = { id: reminder.cust_supp, name: reminder.company_name || reminder.cust_supp };
            }
        },

        popolaFormDaSerie(serie) {
            const savedOwners = this.extractOwnerIds(serie);
            const form = this.emptyForm();
            Object.assign(form, {
                id: serie.id,
                title: serie.title || '',
                description: serie.description || '',
                activity_type: 'periodic',
                execution_mode: 'operational',
                priority: serie.priority || 'media',
                status: serie.status || 'active',
                row_id: serie.row_id ?? null,
                client_id: serie.client_id || (this.ordine ? this.ordine.Customer : null),
                company_name: serie.company_name || (this.ordine ? this.ordine.CompanyName : null),
                job_id: serie.job_id || (this.ordine ? this.ordine.job_id : null),
                job_description: serie.job || (this.ordine ? this.ordine.job_description : null),

                department_id: serie.department_id ?? serie.department?.id ?? null,
                owners: savedOwners,

                scheduling_mode: serie.scheduling_mode || 'date_range',
                allocation_mode: serie.allocation_mode || 'segmented',
                default_duration_hours: serie.default_duration_hours !== undefined && serie.default_duration_hours !== null
                    ? Number(serie.default_duration_hours)
                    : null,
                default_start_time: this.normalizeTimeInput(serie.default_start_time),
                default_end_time: this.normalizeTimeInput(serie.default_end_time),

                recurrence_mode: serie.recurrence_mode || 'fixed',
                recurrence_interval: Number(serie.recurrence_interval || 1),
                recurrence_unit: serie.recurrence_unit || 'month',
                starts_on: this.normalizeDateInput(serie.starts_on),
                ends_on: this.normalizeDateInput(serie.ends_on),
                occurrences_count: serie.occurrences_count !== undefined && serie.occurrences_count !== null
                    ? Number(serie.occurrences_count)
                    : null,
                end_type: serie.end_type || (serie.occurrences_count ? 'count' : 'date'),

                manual_dates: Array.isArray(serie.manual_dates)
                    ? serie.manual_dates.map(entry => ({
                        id: entry.id ?? null,
                        date: this.normalizeDateInput(entry.date || entry.scheduled_date),
                        start_time: this.normalizeTimeInput(entry.start_time),
                        duration_hours: entry.duration_hours !== undefined && entry.duration_hours !== null
                            ? Number(entry.duration_hours)
                            : null,
                        notes: entry.notes || '',
                    }))
                    : (Array.isArray(serie.dates)
                        ? serie.dates.filter(d => d.is_manual).map(d => ({
                            id: d.id ?? null,
                            date: this.normalizeDateInput(d.scheduled_date),
                            start_time: this.normalizeTimeInput(d.start_time),
                            duration_hours: d.duration_hours !== undefined && d.duration_hours !== null ? Number(d.duration_hours) : null,
                            notes: d.notes || '',
                        }))
                        : []),

                propagate_future: false,
            });

            this.formData = form;
            this.seriesStartTimePickerValue = this.timeStringToPickerObject(serie.default_start_time);
            this.seriesEndTimePickerValue = this.timeStringToPickerObject(serie.default_end_time);

            if (serie.company_name) {
                this.selectedClient = { id: serie.client_id, name: serie.company_name };
            }

            if (this.formData.recurrence_mode === 'fixed') {
                this.$nextTick(() => {
                    this.calcolaAnteprima();
                });
            }

            if (this.canManageActivities) {
                if (this.formData.department_id) {
                    this.caricaUtentiReparto(this.formData.department_id, savedOwners);
                } else {
                    this.loadAllAvailableUsers();
                }
            }
        },

        onActivityTypeChange() {
            this.formError = null;
            this.formErrorDetails = [];
            this.allineaStato();
            if (this.formData.activity_type === 'periodic') {
                if (!this.formData.starts_on) {
                    this.formData.starts_on = this.formData.start_date || this.normalizeDateInput(new Date());
                }
                if (!this.formData.ends_on && this.formData.end_date) {
                    this.formData.ends_on = this.formData.end_date;
                }
                if (!this.formData.default_start_time && this.formData.start_datetime) {
                    this.formData.default_start_time = this.formData.start_datetime;
                    this.seriesStartTimePickerValue = this.timeStringToPickerObject(this.formData.start_datetime);
                }
                if (!this.formData.default_duration_hours && this.formData.duration_hours) {
                    this.formData.default_duration_hours = this.formData.duration_hours;
                }
                this.$nextTick(() => {
                    this.calcolaAnteprima();
                });
            }
        },

        allineaStato() {
            const valori = this.statusOptions.map(opt => opt.value);
            if (!valori.includes(this.formData.status)) {
                this.formData.status = valori[0];
            }
        },

        addManualDate() {
            this.formData.manual_dates.push({
                date: '',
                start_time: this.formData.default_start_time || '',
                duration_hours: this.formData.default_duration_hours || null,
                notes: '',
            });
        },

        removeManualDate(index) {
            this.formData.manual_dates.splice(index, 1);
        },

        calcolaAnteprima() {
            this.previewDates = [];

            if (this.formData.recurrence_mode !== 'fixed') return;
            if (!this.formData.starts_on) return;

            const interval = Math.max(1, Number(this.formData.recurrence_interval || 1));
            const max = Math.max(1, Number(this.maxOccurrences || 200));

            const startDate = this.parseLocalDate(this.formData.starts_on);
            if (!startDate) return;

            let endDate = null;
            let requestedCount = null;

            if (this.formData.end_type === 'date') {
                if (!this.formData.ends_on) return;
                endDate = this.parseLocalDate(this.formData.ends_on);
                if (!endDate || endDate < startDate) return;
            } else {
                requestedCount = Math.min(max, Math.max(1, Number(this.formData.occurrences_count || 0)));
                if (!requestedCount) return;
            }

            const generatedDates = [];
            let currentDate = new Date(startDate.getTime());

            while (generatedDates.length < max) {
                if (endDate && currentDate > endDate) break;
                if (requestedCount && generatedDates.length >= requestedCount) break;

                generatedDates.push(this.toDateInputValue(currentDate));

                currentDate = this.addRecurrenceInterval(
                    currentDate,
                    interval,
                    this.formData.recurrence_unit
                );

                if (!currentDate) break;
            }

            this.previewDates = generatedDates;
        },

        addRecurrenceInterval(date, interval, unit) {
            const result = new Date(date.getTime());
            switch (unit) {
                case 'day':
                    result.setDate(result.getDate() + interval);
                    return result;
                case 'week':
                    result.setDate(result.getDate() + interval * 7);
                    return result;
                case 'month':
                    return this.addMonthsClamped(result, interval);
                case 'year':
                    return this.addYearsClamped(result, interval);
                default:
                    return null;
            }
        },

        addMonthsClamped(date, numberOfMonths) {
            const originalDay = date.getDate();
            const result = new Date(date.getTime());
            result.setDate(1);
            result.setMonth(result.getMonth() + numberOfMonths);
            const lastDayOfDestinationMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
            result.setDate(Math.min(originalDay, lastDayOfDestinationMonth));
            return result;
        },

        addYearsClamped(date, numberOfYears) {
            const originalMonth = date.getMonth();
            const originalDay = date.getDate();
            const result = new Date(date.getTime());
            result.setDate(1);
            result.setFullYear(result.getFullYear() + numberOfYears);
            result.setMonth(originalMonth);
            const lastDayOfDestinationMonth = new Date(result.getFullYear(), originalMonth + 1, 0).getDate();
            result.setDate(Math.min(originalDay, lastDayOfDestinationMonth));
            return result;
        },

        extractOwnerIds(item) {
            const users = item.assigned_users || item.owners || item.users || [];
            if (!Array.isArray(users)) return [];
            return users
                .map(user => {
                    if (typeof user === 'string' || typeof user === 'number') return user;
                    return user.id;
                })
                .filter(Boolean);
        },

        nomeAttivita(id) {
            const a = this.allAttivita.find(x => x.id === id);
            return a ? a.title : id;
        },

        startTimeFromInitialStart() {
            const start = new Date(this.initialStart);
            if (Number.isNaN(start.getTime())) return null;
            const h = String(start.getHours()).padStart(2, '0');
            const m = String(start.getMinutes()).padStart(2, '0');
            return `${h}:${m}`;
        },

        timeStringToPickerObject(str) {
            if (!str) return null;
            const parts = String(str).split(':');
            return { hours: parseInt(parts[0], 10) || 0, minutes: parseInt(parts[1], 10) || 0, seconds: 0 };
        },

        pickerObjectToTimeString(val) {
            if (!val) return null;
            if (typeof val === 'string') return val.length > 5 ? val.substring(0, 5) : val;
            const h = String(val.hours ?? 0).padStart(2, '0');
            const m = String(val.minutes ?? 0).padStart(2, '0');
            return `${h}:${m}`;
        },

        updateDeliveryDate() {
            const rawStart = this.formData.start_date;
            if (!rawStart) return;
            const startStr = typeof rawStart === 'string' ? rawStart : this.formatLocalDate(new Date(rawStart));
            const startDate = new Date(`${startStr.substring(0, 10)}T00:00:00`);
            if (Number.isNaN(startDate.getTime())) return;
            const rawDelivery = this.formData.delivery_date;
            const deliveryStr = rawDelivery ? (typeof rawDelivery === 'string' ? rawDelivery : this.formatLocalDate(new Date(rawDelivery))) : null;
            const deliveryDate = deliveryStr ? new Date(`${deliveryStr.substring(0, 10)}T00:00:00`) : null;
            if (!deliveryDate || Number.isNaN(deliveryDate.getTime()) || startDate >= deliveryDate) {
                const newDeliveryDate = new Date(startDate);
                newDeliveryDate.setDate(newDeliveryDate.getDate() + 1);
                this.formData.delivery_date = this.formatLocalDate(newDeliveryDate);
            }
        },

        formatLocalDate(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        formatDateDisplay(value) {
            if (!value) return '-';
            const date = this.parseLocalDate(value);
            if (!date || Number.isNaN(date.getTime())) return value;
            return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
        },

        parseLocalDate(value) {
            if (!value) return null;
            if (value instanceof Date) return new Date(value.getTime());
            const normalized = String(value).substring(0, 10);
            const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (match) {
                const year = Number(match[1]);
                const month = Number(match[2]) - 1;
                const day = Number(match[3]);
                const date = new Date(year, month, day);
                if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
                return date;
            }
            const fallbackDate = new Date(value);
            return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
        },

        normalizeDateInput(value) {
            const date = this.parseLocalDate(value);
            return date ? this.toDateInputValue(date) : '';
        },

        toDateInputValue(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        normalizeTimeInput(value) {
            if (!value) return '';
            const time = String(value);
            if (/^\d{2}:\d{2}/.test(time)) return time.substring(0, 5);
            return '';
        },

        nullableNumber(value) {
            if (value === null || value === undefined || value === '') return null;
            const number = Number(value);
            return Number.isFinite(number) ? number : null;
        },

        statusLabel(status) {
            const map = {
                nuova: 'Nuova',
                assegnata: 'Assegnata',
                schedulata: 'Schedulata',
                in_lavorazione: 'In lavorazione',
                completata: 'Completata',
                active: 'Attiva',
                suspended: 'Sospesa',
            };
            return map[status] || status || '-';
        },

        statusBadgeClass(status) {
            return {
                'bg-gray-100 text-gray-700': status === 'nuova',
                'bg-blue-100 text-blue-700': status === 'assegnata',
                'bg-purple-100 text-purple-700': status === 'schedulata',
                'bg-orange-100 text-orange-700': status === 'in_lavorazione',
                'bg-green-100 text-green-700': status === 'completata' || status === 'active',
                'bg-amber-100 text-amber-700': status === 'suspended',
            };
        },

        priorityBadgeClass(priority) {
            return {
                'bg-green-100 text-green-700': priority === 'bassa',
                'bg-yellow-100 text-yellow-700': priority === 'media',
                'bg-orange-100 text-orange-700': priority === 'alta',
                'bg-red-100 text-red-700': priority === 'critica',
            };
        },

        async onDepartmentChange() {
            this.formData.owners = [];
            this.localAssignableUsers = [];

            if (!this.formData.department_id) {
                await this.loadAllAvailableUsers();
                return;
            }

            await this.caricaUtentiReparto(this.formData.department_id);
        },

        async loadAllAvailableUsers() {
            this.loadingDeptUsers = true;
            try {
                const resp = await axios.get(route('modules.attivita.departments.users.available'));
                this.localAssignableUsers = Array.isArray(resp.data) ? resp.data : [];
            } catch (e) {
                console.error('Errore caricamento utenti disponibili:', e);
                this.localAssignableUsers = [];
            } finally {
                this.loadingDeptUsers = false;
            }
        },

        async caricaUtentiReparto(departmentId, selectedOwnerIds = []) {
            if (!departmentId) {
                await this.loadAllAvailableUsers();
                return;
            }

            this.loadingDeptUsers = true;
            try {
                const resp = await axios.get(route('modules.attivita.departments.show', { department: departmentId }));
                const users = resp.data?.users || resp.data?.data || [];
                this.localAssignableUsers = Array.isArray(users) ? users : [];

                const availableIds = new Set(this.localAssignableUsers.map(u => String(u.id)));
                this.formData.owners = (selectedOwnerIds || []).filter(ownerId => availableIds.has(String(ownerId)));
            } catch (error) {
                if (error?.response?.status === 403) {
                    await this.loadAllAvailableUsers();
                } else {
                    this.localAssignableUsers = [];
                }
            } finally {
                this.loadingDeptUsers = false;
            }
        },

        searchClients(search, loading) {
            if (!search) {
                this.foundClients = [];
                return;
            }
            loading(true);
            clearTimeout(this._searchClientsTimeout);
            this._searchClientsTimeout = setTimeout(() => {
                axios.get(route('modules.contratti.ricerca-clienti', search))
                    .then(response => {
                        this.foundClients = response.data || [];
                    })
                    .finally(() => loading(false));
            }, 300);
        },

        onClientSelect(selectedClient) {
            this.formData.company_name = selectedClient ? selectedClient.name : null;
            this.formData.client_id = selectedClient ? selectedClient.id : null;
            this.formData.job_id = null;
            this.formData.job_description = null;
            this.jobs = [];
            if (selectedClient) {
                this.getJobs(selectedClient.id);
            }
        },

        getJobs(clientId) {
            axios.get(route('modules.contratti.commesse', clientId))
                .then(response => {
                    this.jobs = response.data || [];
                })
                .catch(() => {
                    this.jobs = [];
                });
        },

        onJobSelect() {
            const job = this.jobs.find(j => j.id === this.formData.job_id);
            this.formData.job_description = job ? job.name : null;
        },

        chiudi() {
            this.$emit('update:modelValue', false);
            this.$emit('close');
        },

        validateForm() {
            if (!this.formData.title || !this.formData.title.trim()) {
                return 'Il titolo è obbligatorio.';
            }

            if (!this.isOrdineContext && !this.isReminderMode && !this.formData.client_id) {
                return 'Il cliente è obbligatorio.';
            }

            // Validazione Standard
            if (this.formData.activity_type === 'standard') {
                if (this.formData.scheduling_mode === 'date_range' && !this.formData.end_date) {
                    return 'La data fine è obbligatoria.';
                }
                if (this.formData.scheduling_mode === 'duration' && !this.formData.duration_hours) {
                    return 'La durata prevista è obbligatoria.';
                }
            }

            // Validazione Periodica / Serie (Sempre operativa)
            if (this.formData.activity_type === 'periodic') {
                if (this.formData.scheduling_mode === 'duration' && (!this.formData.default_duration_hours || Number(this.formData.default_duration_hours) <= 0)) {
                    return 'Inserisci una durata prevista valida per la serie.';
                }
                if (this.formData.scheduling_mode === 'date_range' && this.formData.default_start_time && this.formData.default_end_time && this.formData.default_end_time <= this.formData.default_start_time) {
                    return "L'ora di fine deve essere successiva all'ora di inizio.";
                }

                if (this.formData.recurrence_mode === 'fixed') {
                    if (!this.formData.starts_on) {
                        return 'La prima data della ricorrenza è obbligatoria.';
                    }
                    if (!this.formData.recurrence_interval || Number(this.formData.recurrence_interval) < 1) {
                        return "L'intervallo di ricorrenza deve essere almeno 1.";
                    }
                    if (this.formData.end_type === 'date' && !this.formData.ends_on) {
                        return 'Inserisci la data finale della ricorrenza.';
                    }
                    if (this.formData.end_type === 'date' && this.formData.ends_on < this.formData.starts_on) {
                        return 'La data finale non può precedere la prima data.';
                    }
                    if (this.formData.end_type === 'count' && (!this.formData.occurrences_count || Number(this.formData.occurrences_count) < 1)) {
                        return 'Inserisci un numero valido di occorrenze.';
                    }
                }

                if (this.formData.recurrence_mode === 'manual') {
                    const manualDates = this.formData.manual_dates || [];
                    if (manualDates.length === 0) {
                        return 'Aggiungi almeno una data manuale.';
                    }
                    if (manualDates.some(e => !e.date)) {
                        return 'Compila tutte le date manuali inserite.';
                    }
                    const normalizedDates = manualDates.map(e => e.date);
                    if (new Set(normalizedDates).size !== normalizedDates.length) {
                        return 'Non è possibile inserire due volte la stessa data manuale.';
                    }
                }
            }

            return null;
        },

        async salva() {
            this.formError = null;
            this.formErrorDetails = [];

            const validationError = this.validateForm();
            if (validationError) {
                this.formError = validationError;
                this.$nextTick(() => {
                    this.$refs.modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
                });
                return;
            }

            this.salvando = true;

            try {
                if (this.isReminderMode) {
                    const isDuration = this.formData.scheduling_mode === 'duration';
                    const now = new Date();
                    const nowDate = this.formatLocalDate(now);
                    const nowTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                    const currentUserId = this.$page.props.auth?.user?.id ?? null;
                    const payload = {
                        title: this.formData.title.trim(),
                        description: this.formData.description?.trim() || null,
                        department_id: this.formData.department_id || null,
                        priority: this.formData.priority,
                        status: this.formData.status,
                        client_id: this.formData.client_id || null,
                        company_name: this.formData.company_name || null,
                        job_id: this.formData.job_id || null,
                        job_description: this.formData.job_description || null,
                        scheduling_mode: this.formData.scheduling_mode,
                        allocation_mode: this.formData.allocation_mode,
                        start_date: nowDate,
                        start_datetime: nowTime,
                        end_date: isDuration ? null : this.formData.end_date,
                        end_datetime: isDuration ? null : this.formData.end_datetime,
                        duration_hours: isDuration ? this.nullableNumber(this.formData.duration_hours) : null,
                        delivery_date: this.formData.delivery_date || null,
                        owners: currentUserId !== null ? [String(currentUserId)] : [],
                    };

                    const response = await axios.post(
                        route('modules.attivita.promemoria.prendi-in-carico', this.reminder.id),
                        payload
                    );

                    this.$emit('saved', {
                        response: response.data,
                        isEdit: false,
                        isSeries: false,
                        isReminder: true,
                        reminderId: this.reminder.id,
                        payload,
                    });
                    this.chiudi();
                } else if (this.formData.activity_type === 'periodic') {
                    // Salvataggio serie periodica (sempre execution_mode: operational)
                    const payload = {
                        order_id: this.isOrdineContext ? (this.ordine.SaleOrdId || null) : (this.formData.order_id || null),
                        row_id: this.formData.row_id || null,
                        job_id: this.isOrdineContext ? (this.ordine.job_id || null) : (this.formData.job_id || null),
                        job: this.isOrdineContext ? (this.ordine.job_description || null) : (this.formData.job_description || null),
                        client_id: this.isOrdineContext ? (this.ordine.Customer || null) : (this.formData.client_id || null),
                        company_name: this.isOrdineContext ? (this.ordine.CompanyName || null) : (this.formData.company_name || null),

                        title: this.formData.title.trim(),
                        description: this.formData.description?.trim() || null,
                        activity_type: 'standard',
                        execution_mode: 'operational',
                        priority: this.formData.priority,
                        status: this.formData.status,

                        scheduling_mode: this.formData.scheduling_mode,
                        allocation_mode: this.formData.allocation_mode,
                        default_duration_hours: this.formData.scheduling_mode === 'duration'
                            ? this.nullableNumber(this.formData.default_duration_hours)
                            : null,
                        default_start_time: this.formData.default_start_time || null,
                        default_end_time: this.formData.scheduling_mode === 'date_range'
                            ? (this.formData.default_end_time || null)
                            : null,

                        department_id: this.canManageActivities ? (this.formData.department_id || null) : null,
                        owners: this.canManageActivities ? [...(this.formData.owners || [])].map(String) : [],

                        recurrence_mode: this.formData.recurrence_mode,
                        recurrence_interval: this.formData.recurrence_mode === 'fixed'
                            ? Number(this.formData.recurrence_interval)
                            : null,
                        recurrence_unit: this.formData.recurrence_mode === 'fixed'
                            ? this.formData.recurrence_unit
                            : null,
                        starts_on: this.formData.recurrence_mode === 'fixed'
                            ? this.formData.starts_on
                            : null,
                        ends_on: this.formData.recurrence_mode === 'fixed' && this.formData.end_type === 'date'
                            ? this.formData.ends_on
                            : null,
                        occurrences_count: this.formData.recurrence_mode === 'fixed' && this.formData.end_type === 'count'
                            ? Number(this.formData.occurrences_count)
                            : null,
                        end_type: this.formData.recurrence_mode === 'fixed'
                            ? this.formData.end_type
                            : null,

                        dates: this.formData.recurrence_mode === 'manual'
                            ? (this.formData.manual_dates || []).map(entry => ({
                                id: entry.id || null,
                                date: entry.date,
                                start_time: entry.start_time || null,
                                duration_hours: this.nullableNumber(entry.duration_hours),
                                notes: entry.notes?.trim() || null,
                            }))
                            : null,

                        propagate_future: this.isSeriesEdit ? Boolean(this.formData.propagate_future) : false,
                    };

                    let response;
                    if (this.isSeriesEdit) {
                        response = await axios.patch(
                            route('modules.attivita.series.update', { series: this.formData.id }),
                            payload
                        );
                    } else {
                        response = await axios.post(
                            route('modules.attivita.series.store'),
                            payload
                        );
                    }

                    this.$emit('saved', { response: response.data, isEdit: this.isEdit, isSeries: true, payload });
                    this.chiudi();
                } else {
                    // Salvataggio attività standard
                    const isDuration = this.formData.scheduling_mode === 'duration';
                    const payload = {
                        ...this.formData,
                        end_date: isDuration ? null : this.formData.end_date,
                        end_datetime: isDuration ? null : this.formData.end_datetime,
                    };

                    let url;
                    if (this.isOrdineContext) {
                        Object.assign(payload, {
                            order_id: this.ordine.SaleOrdId || null,
                            job_id: this.ordine.job_id || null,
                            job: this.ordine.job_description || null,
                            client_id: this.ordine.Customer || null,
                            company_name: this.ordine.CompanyName || null,
                        });
                        url = route('modules.attivita.salva-attivita-ordine');
                    } else {
                        url = this.isEdit
                            ? route('modules.attivita.aggiorna-attivita')
                            : route('modules.attivita.crea-attivita');
                    }

                    const response = await axios.post(url, payload);
                    this.$emit('saved', { response: response.data, isEdit: this.isEdit, isSeries: false, payload });
                    this.chiudi();
                }
            } catch (e) {
                const data = e.response?.data;
                if (data?.type === 'resource_capacity_error') {
                    const d = data.details || {};
                    this.formError = `${d.resource_name || 'La risorsa selezionata'} non ha disponibilità sufficiente per pianificare${d.required_hours ? ' ' + d.required_hours + ' ore' : ''} entro il ${d.delivery_date || 'termine'}. Modifica la data di consegna, riduci la durata oppure scegli un'altra risorsa.`;
                } else if (data?.type === 'resource_overlap_error') {
                    const d = data.details || {};
                    this.formError = `${d.resource_name || 'La risorsa selezionata'} ha già attività pianificate in questo intervallo. Cambia orario, scegli un'altra risorsa oppure rimuovi il conflitto esistente.`;
                } else if (data?.errors) {
                    this.formError = data.message || 'I dati inseriti non sono validi.';
                    this.formErrorDetails = Object.values(data.errors).flat().filter(Boolean);
                } else {
                    this.formError = data?.message || data?.error || 'Errore durante il salvataggio.';
                }
                this.$nextTick(() => {
                    this.$refs.modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
                });
            } finally {
                this.salvando = false;
            }
        },
    },
};
</script>

<style scoped>
.action-panel-fade-enter-active,
.action-panel-fade-leave-active {
    transition: opacity 0.2s ease;
}

.action-panel-fade-enter-from,
.action-panel-fade-leave-to {
    opacity: 0;
}

.action-panel-slide-enter-active,
.action-panel-slide-leave-active {
    transition: transform 0.25s ease;
}

.action-panel-slide-enter-from,
.action-panel-slide-leave-to {
    transform: translateX(100%);
}

@media (max-width: 1024px) {
    aside {
        max-width: 85vw;
    }
}

@media (max-width: 768px) {
    aside {
        max-width: 100vw;
        border-radius: 0;
    }
}
</style>
